import { inject, Injectable } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core';
import { ADD_TO_FAVORITES_MUTATION, MY_FAVORITES_QUERY, REMOVE_FROM_FAVORITES_MUTATION } from '@shared/consts/graphql';
import { CreateFavoriteInput, Favorite, FavoriteType } from '@shared/graphql/generated';
import { FavoriteModelStoreService } from '@shared/stores/favorite-model-store.service';
import { FavoriteSeriesStoreService } from '@shared/stores/favorite-series-store.service';
import { Apollo } from 'apollo-angular';
import { catchError, map, Observable, of, take, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FavoriteService {
  private apollo = inject(Apollo);
  private seriesStore = inject(FavoriteSeriesStoreService);
  private modelStore = inject(FavoriteModelStoreService);

  constructor() {
  }

  addToFavorites(input: CreateFavoriteInput): Observable<boolean> {
    return this.apollo.mutate({
      mutation: ADD_TO_FAVORITES_MUTATION,
      variables: { input }
    })
      .pipe(
        map((result: ApolloQueryResult<{ createFavorite: Favorite }>) => result.data && result.data?.createFavorite),
        tap((favorite: Favorite) => {
          if (!favorite) return;

          const { model, series } = favorite;

          if (!!model) this.modelStore.addToList(favorite.model);
          if (!!series) this.seriesStore.addToList(favorite.series);
        }),
        map((favorite: Favorite) => !!favorite),
      );
  }

  clearFavorites(): void {
    this.modelStore.list = undefined;
    this.seriesStore.list = undefined;
  }

  init(): void {
    this.apollo.watchQuery({ query: MY_FAVORITES_QUERY }).valueChanges
      .pipe(
        take(1),
        map((result: ApolloQueryResult<{ myFavorites: Favorite[] }>) => result.data.myFavorites),
      )
      .subscribe({
        next: (favorites: Favorite[]) => {
          this.modelStore.list = favorites.filter((favorite: Favorite) => !!favorite.model).map((favorite: Favorite) => favorite.model);
          this.seriesStore.list = favorites.filter((favorite: Favorite) => !!favorite.series).map((favorite: Favorite) => favorite.series);
        },
      });
  }

  removeFromFavorites(input: CreateFavoriteInput): Observable<boolean> {
    return this.apollo.mutate({
      mutation: REMOVE_FROM_FAVORITES_MUTATION,
      variables: { input }
    })
      .pipe(
        map((result: ApolloQueryResult<{ deleteMyFavorite: number }>) => result.data && result.data?.deleteMyFavorite),
        map((result: number) => !!result),
      );
  }
}
